import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { CloseIcon as SearchCloseIcon } from 'src/shared/design-system/DishFirst/DishModal/components/Buttons/styles';
import { useFeatureFlag } from 'src/shared/design-system/FeatureFlag';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { Routes } from 'src/shared/Routes';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { useIsOnSearchPage } from 'src/shared/utils/SearchUtils';
import { useSelector } from 'src/store';
import { selectAvailableCartItemCount } from 'src/store/cart/selectors';
import { CONTENT_ID } from 'src/store/navbar';
import { TopShefFilterButton, TopShefFilterButtonDivider } from 'src/top-shef/TopShefFilterButton';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import Pill from 'web-common/src/shared/design-system/components/Button/Pill';
import { CuisineFilterButton } from '../../dish-first/explore/components/ExploreFilterButtons/CuisineFilterButton';
import { DateFilterButton } from '../../dish-first/explore/components/ExploreFilterButtons/DateFilterButton';
import { DietaryFilterButton } from '../../dish-first/explore/components/ExploreFilterButtons/DietaryFilterButton';
import { MoreFiltersButton } from '../../dish-first/explore/components/ExploreFilterButtons/MoreFiltersButton';
import { ProteinFilterButton } from '../../dish-first/explore/components/ExploreFilterButtons/ProteinFilterButton';
import { CuisineFilterModal } from '../../dish-first/explore/components/ExploreFilterModals/CuisineFilterModal';
import { SubCuisineFilterModal } from '../../dish-first/explore/components/ExploreFilterModals/CuisineFilterModal/SubCuisineFilterModal';
import { DateFilterModal } from '../../dish-first/explore/components/ExploreFilterModals/DateFilterModal';
import { DietaryFilterModal } from '../../dish-first/explore/components/ExploreFilterModals/DietaryFilterModal';
import { MoreFiltersModal } from '../../dish-first/explore/components/ExploreFilterModals/MoreFiltersModal';
import { ProteinFilterModal } from '../../dish-first/explore/components/ExploreFilterModals/ProteinFilterModal';
import { ExploreFilterIds } from '../../dish-first/explore/components/ExploreFilters';
import { MealPlanSubscriptionContext } from '../../meal-plans/MealPlanSubscriptionsContext';
import { AccountSettingsLinks } from '../header-v2/RightNav/AccountSettingsLinks';
import { CartNavItem } from '../header-v2/shared-components/CartNavItem';
import { HamburgerMenu } from '../header-v2/shared-components/HamburgerMenu';
import { ShefLogo } from '../header-v2/shared-components/ShefLogo';
import { DishFirstSearch } from '../search/DishFirstSearch';
import { SearchDropdown } from '../search/DishFirstSearch/SearchDropdown';
import { BackButton } from './BackButton';
import { HEADER, LOGO_HEIGHT, NAV_HEIGHT_VALUE } from './consts';
import { useWindowScroll } from './hooks/useWindowScroll';
import { MainContainer } from './MainContainer';
import { PromoContent } from './PromoContent';
import {
  useBackButtonReducer,
  useCuisineFiltersReducer,
  useDatePickerReducer,
  useDietaryFiltersReducer,
  useFilterBarReducer,
  useHideNavbar,
  useMoreFiltersReducer,
  useNavbarState,
  useProteinFiltersReducer,
  useSearchReducer,
  useSubCuisineFiltersReducer,
} from './store';
import { Cart, Content, FilterBar, Logo, Main, Menu, MenuBar, Modal } from './styles';
import { NavigationProps } from './types';
import { checkElementHeight } from './utils';

export const DishFirstHeader: React.FC<NavigationProps> = React.memo(
  ({ backgroundRef, hideSearchAndSignup = false }) => {
    const [currentUser] = useCurrentUser();
    const numItemsInCart = useSelector((state) => selectAvailableCartItemCount(state.cart));
    const history = useHistory();
    const { location } = history;
    const isExplore = locationInRoutes(location, [Routes.CONSUMER_EXPLORE, Routes.CONSUMER_ORDER_SEARCH]);
    const isShefMenuPage = locationInRoutes(location, [Routes.CONSUMER_ORDER_ONE_TIME]);
    const isMealPlanUpcomingOrderPage = locationInRoutes(location, [Routes.CONSUMER_MEAL_PLANS_UPCOMING_ORDER]);
    const isHomepage = locationInRoutes(location, [Routes.CONSUMER_LANDING]);

    const showManageMealPlans = useFeatureFlag('showManageMealPlans');
    const [datePickerEl, setDatePickerEl] = useState<Element | null>();
    const [cuisineCarouselEl, setCuisineCarouselEl] = useState<Element | null>();
    const [filterButtonRowEl, setFilterButtonRowEl] = useState<Element | null>();

    // TODO Trevomatic - Clean this up so we have a single reducer controlling the filter modals
    const { isShowing: cuisineFiltersIsShowing } = useCuisineFiltersReducer();
    const { isShowing: datePickerIsShowing } = useDatePickerReducer();
    const { isShowing: dietaryFiltersIsShowing } = useDietaryFiltersReducer();
    const { isShowing: moreFiltersIsShowing } = useMoreFiltersReducer();
    const { isShowing: proteinFiltersIsShowing } = useProteinFiltersReducer();
    const { isShowing: searchIsShowing } = useSearchReducer();
    const { isShowing: subCuisineFiltersIsShowing } = useSubCuisineFiltersReducer();
    const { isShowing: backButtonIsShowing } = useBackButtonReducer();
    const { isShowing: filterBarIsShowing } = useFilterBarReducer();

    const { animationsIsShowing, isContentShowing, menuBarIsShowing } = useNavbarState();

    const {
      hideAll,
      hideCuisineFilters,
      hideDatePicker,
      hideDietaryFilters,
      hideMoreFilters,
      hideProteinFilters,
      hideSearch,
      hideSubCuisineFilters,
    } = useHideNavbar();
    const { isOnSearchPageMobile } = useIsOnSearchPage();
    const [scrollingUp, setScrollingUp] = useState<boolean>(false);
    const [shouldShowDate, setShouldShowDate] = useState<boolean>(false);
    const [shouldShowCuisine, setShouldShowCuisine] = useState<boolean>(false);
    const [shouldShowFilters, setShouldShowFilters] = useState<boolean>(false);

    const consumerOrderOneTimeMatch = useRouteMatch<{ id?: string }>(Routes.CONSUMER_ORDER_ONE_TIME);
    const shefId = consumerOrderOneTimeMatch?.params?.id ?? undefined;

    const cuisineCarousel = document.getElementById(ExploreFilterIds.CuisineCarousel);
    const datePicker = document.getElementById(ExploreFilterIds.DatePicker);
    const filterButtonRow = document.getElementById(ExploreFilterIds.FilterButtonRow);
    const { isSmallScreen } = useScreenContext();
    const { userHasActiveMealPlan } = useContext(MealPlanSubscriptionContext);

    const hideFilterButtons = useCallback(() => {
      setShouldShowCuisine(false);
      setShouldShowDate(false);
      setShouldShowFilters(false);
    }, [setShouldShowCuisine, setShouldShowDate, setShouldShowFilters]);

    useEffect(() => {
      setDatePickerEl(datePicker);
      setCuisineCarouselEl(cuisineCarousel);
      setFilterButtonRowEl(filterButtonRow);
      if (window.scrollY < NAV_HEIGHT_VALUE) hideFilterButtons();
    }, [cuisineCarousel, datePicker, filterButtonRow, hideFilterButtons]);

    const scrollListener = useCallback(
      (isUp) => {
        const buffer = isSmallScreen ? NAV_HEIGHT_VALUE : 0;
        setScrollingUp(isUp);
        setShouldShowDate(checkElementHeight(datePickerEl, isSmallScreen));
        setShouldShowCuisine(checkElementHeight(cuisineCarouselEl ?? filterButtonRowEl, isSmallScreen, buffer));
        setShouldShowFilters(checkElementHeight(filterButtonRowEl, isSmallScreen, buffer));
      },
      [
        setScrollingUp,
        setShouldShowCuisine,
        setShouldShowDate,
        setShouldShowFilters,
        datePickerEl,
        cuisineCarouselEl,
        filterButtonRowEl,
        isSmallScreen,
      ]
    );

    useWindowScroll(scrollListener);
    const isOnPageWithFilterBar = isExplore || isShefMenuPage;
    const shouldShowFilterButtons = !searchIsShowing && isOnPageWithFilterBar;
    return (
      <MainContainer showModal={isContentShowing}>
        <Modal.Backdrop show={isContentShowing} onClick={hideAll} />
        <Main.Wrapper largeHeader={isOnSearchPageMobile}>
          <Menu.Container>
            <Menu.Wrapper hideOnMobile={backButtonIsShowing}>
              <HamburgerMenu />
            </Menu.Wrapper>
            <BackButton showOnMobile={backButtonIsShowing} />
          </Menu.Container>
          <Main.ContentContainer>
            <Logo.Container hide={shouldShowDate}>
              <ShefLogo height={LOGO_HEIGHT} />
            </Logo.Container>
            {shouldShowFilterButtons && (
              <>
                <Content.Container>
                  <Content.Wrapper show={shouldShowDate || isShefMenuPage} animate>
                    <DateFilterButton />
                  </Content.Wrapper>
                </Content.Container>
                <FilterBar.Inline>
                  {!isShefMenuPage && (
                    <>
                      <FilterBar.Container show={shouldShowCuisine || isContentShowing} animate>
                        <TopShefFilterButton />
                        <TopShefFilterButtonDivider className='ml-1.5' />
                      </FilterBar.Container>
                    </>
                  )}
                  {!isShefMenuPage && (
                    <FilterBar.Container show={shouldShowCuisine || isContentShowing} animate>
                      <CuisineFilterButton />
                    </FilterBar.Container>
                  )}
                  <FilterBar.Container show={shouldShowFilters || isContentShowing} animate smallMargin>
                    {isShefMenuPage && <ProteinFilterButton />}
                    <DietaryFilterButton />
                    <MoreFiltersButton />
                  </FilterBar.Container>
                </FilterBar.Inline>
              </>
            )}
            {!hideSearchAndSignup && !isHomepage && <DishFirstSearch backgroundRef={backgroundRef} />}
            {searchIsShowing && <SearchCloseIcon onClick={hideSearch} style={{ cursor: 'pointer' }} />}
          </Main.ContentContainer>
          {!searchIsShowing && !hideSearchAndSignup && (
            <Cart.Container>
              {!currentUser && !numItemsInCart ? <AccountSettingsLinks /> : <CartNavItem />}
            </Cart.Container>
          )}
          {userHasActiveMealPlan && isMealPlanUpcomingOrderPage && showManageMealPlans && (
            <Cart.Container>
              <Pill onClick={() => history.push(Routes.CONSUMER_MEAL_PLANS_MANAGE)}>Manage Service</Pill>
            </Cart.Container>
          )}
        </Main.Wrapper>
        {!hideSearchAndSignup && <PromoContent isContentShowing={isContentShowing && !hideSearchAndSignup} />}
        {isOnPageWithFilterBar && (
          <FilterBar.Row hide={!shouldShowFilters} isContentShowing={isContentShowing} isScrollingUp={scrollingUp}>
            <FilterBar.Container animate={true} show={true} isContentShowing={isContentShowing}>
              {!isShefMenuPage && (
                <>
                  <TopShefFilterButton className='shrink-0' />
                  <TopShefFilterButtonDivider className='mx-2 shrink-0' />
                </>
              )}
              {!isShefMenuPage && <CuisineFilterButton />}
              {isShefMenuPage && <ProteinFilterButton />}
              <DietaryFilterButton />
              <MoreFiltersButton />
            </FilterBar.Container>
          </FilterBar.Row>
        )}
        <MenuBar.Row
          filterBarHidden={isContentShowing || !filterBarIsShowing}
          hide={!menuBarIsShowing}
          isContentShowing={isContentShowing}
          isScrollingUp={scrollingUp}>
          <MenuBar.Container animate={animationsIsShowing} show={menuBarIsShowing} id={CONTENT_ID.MENU_BAR} />
        </MenuBar.Row>
        <Modal.Container show={isContentShowing}>
          <SearchDropdown />

          <DietaryFilterModal
            header={HEADER.DIETARY_FILTERS}
            isShowing={dietaryFiltersIsShowing}
            largeHeader={isOnSearchPageMobile}
            onHide={hideDietaryFilters}
          />

          {isShefMenuPage && (
            <ProteinFilterModal
              header={HEADER.PROTEIN_FILTERS}
              isShowing={proteinFiltersIsShowing}
              largeHeader={isOnSearchPageMobile}
              onHide={hideProteinFilters}
            />
          )}

          <DateFilterModal isShowing={datePickerIsShowing} onHide={hideDatePicker} shefId={shefId} />

          <CuisineFilterModal isShowing={cuisineFiltersIsShowing} onHide={hideCuisineFilters} />

          <SubCuisineFilterModal isShowing={subCuisineFiltersIsShowing} onHide={hideSubCuisineFilters} />

          <MoreFiltersModal
            header={HEADER.MORE_FILTERS}
            isShowing={moreFiltersIsShowing}
            largeHeader={isOnSearchPageMobile}
            onHide={hideMoreFilters}
          />
        </Modal.Container>
      </MainContainer>
    );
  }
);

DishFirstHeader.displayName = 'DishFirstHeader';

export default DishFirstHeader;
